import { createContext, useCallback, useContext, useEffect, useState } from "react";
import CookieConsentDialog from "../components/CookieConsentDialog";

/*
    Cookie/tracking consent for the webapp.

    The decision is stored by public/js/cookie-consent.js, which is shared
    verbatim with the homepage and writes a cookie on .underwaterly.com - so a
    visitor who already decided on underwaterly.com is not asked a second time
    here, and a decision made here applies over there too. This provider only
    owns the UI: when to show the banner, and which panel to show.

    Usage:
    - src/index.js: wrap the application in CookieConsentProvider
    - Any component: const { openPreferences } = useCookieConsent();
      Call openPreferences() to let the visitor change or withdraw their
      decision later - required, and the reason Settings and the logged-out
      pages both carry a "Cookie settings" entry point.
*/

const CookieConsentContext = createContext({
  consent: null,
  available: false,
  openPreferences: () => {},
});

export default function CookieConsentProvider({ children }) {
  // null until the visitor has decided (or while an older decision is stale
  // after a CONSENT_VERSION bump), which is exactly when we have to ask.
  const [consent, setConsent] = useState(null);
  const [available, setAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState("summary");

  useEffect(() => {
    const store = window.underwaterlyConsent;

    // The consent store is blocked or failed to load. Without it we cannot
    // record a decision, so showing the banner would be dishonest - stay
    // quiet; src/utils/analytics.js keeps analytics off in the same case.
    if (!store) return undefined;

    setAvailable(true);

    // Fires once immediately with the stored decision, and again on every
    // later change (including ones made in this tab by another component).
    return store.onChange((value) => {
      setConsent(value);
      if (!value) {
        setPanel("summary");
        setOpen(true);
      }
    });
  }, []);

  const openPreferences = useCallback(() => {
    setPanel("prefs");
    setOpen(true);
  }, []);

  const handleDecide = useCallback((analytics) => {
    // Writing the decision notifies every listener, so analytics.js picks it
    // up in the same tick - no reload needed to start or stop gtag.js.
    window.underwaterlyConsent.setConsent({ analytics });
    setOpen(false);
  }, []);

  return (
    <CookieConsentContext.Provider value={{ consent, available, openPreferences }}>
      {children}
      <CookieConsentDialog
        open={open}
        panel={panel}
        setPanel={setPanel}
        consent={consent}
        onDecide={handleDecide}
        onClose={() => setOpen(false)}
      />
    </CookieConsentContext.Provider>
  );
}

// Custom hook to consume the cookie consent context
export function useCookieConsent() {
  return useContext(CookieConsentContext);
}
